import React, { useEffect, useRef, useState } from "react";

// Modules
import moment from "moment";

// UI components
import { Button, IconButton, TextField, Typography } from "@mui/material";
import SendIcon from "@mui/icons-material/SendOutlined";
import CloseIcon from "@mui/icons-material/CloseOutlined";

/**
 * Presentational message thread shared by the buyer portal and the seller
 * dashboard. Data fetching/posting lives in the callers — this component
 * only renders rows ({id, author_name, is_seller, body, created}) and a
 * composer.
 *
 * `mine` decides which side of the thread is highlighted: 'seller' on the
 * dashboard, 'buyer' in the portal.
 */
export default function RoomThread(props) {
  const {
    messages,
    onSend,
    canPost = true,
    mine = "buyer",
    // Typography owner: "client" in the buyer portal, "admin" on the
    // seller dashboard — keeps the thread in each context's type scale.
    owner = "client",
    identitySlot = null,
    // Optional composer scope ({label, onClear}) shown as a dismissible chip
    // — e.g. "Discussing: Proposal.pdf" or "Content request".
    scope = null,
    emptyText = "No messages yet — start the conversation.",
  } = props;

  const [draft, setDraft] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const endRef = useRef(null);

  useEffect(() => {
    if (endRef.current) {
      endRef.current.scrollIntoView({ block: "end" });
    }
  }, [messages.length]);

  const send = () => {
    const body = draft.trim();
    if (!body || isSending) {
      return;
    }

    setIsSending(true);
    setSendError(null);

    Promise.resolve(onSend(body))
      .then(() => {
        setDraft("");
        setIsSending(false);
      })
      .catch((error) => {
        setIsSending(false);
        setSendError(
          error?.message || "Your message couldn't be sent. Please try again."
        );
      });
  };

  return (
    <div className="room-thread">
      <div className="room-thread__list">
        {messages.length === 0 && (
          <Typography owner={owner} variant="body2" className="room-thread__empty">
            {emptyText}
          </Typography>
        )}
        {messages.map((message) => {
          const isMine =
            (mine === "seller" && message.is_seller) ||
            (mine === "buyer" && !message.is_seller);
          return (
            <div
              className={`room-thread__message ${
                isMine ? "room-thread__message--mine" : ""
              }`}
              key={`msg-${message.id}`}
            >
              <div className="room-thread__meta">
                <Typography owner={owner} variant="caption" className="room-thread__author">
                  {message.author_name}
                  {message.is_seller ? " · seller" : ""}
                </Typography>
                <Typography owner={owner} variant="caption" className="room-thread__time">
                  {moment.unix(message.created).format("DD MMM HH:mm")}
                </Typography>
              </div>
              <div
                className={`room-thread__bubble ${
                  message.kind === "request" ? "room-thread__bubble--request" : ""
                }`}
              >
                {message.kind === "request" && (
                  <span className="room-thread__chip">Content request</span>
                )}
                <Typography owner={owner} variant="body2">{message.body}</Typography>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      {canPost && (
        <div className="room-thread__composer">
          {scope && (
            <div className="room-thread__scope mb-2">
              <Typography owner={owner} variant="body2">
                {scope.label}
              </Typography>
              <IconButton size="small" onClick={scope.onClear} title="Clear">
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>
          )}
          {identitySlot}
          <div className="d-flex gap-2 align-items-start">
            <TextField
              className="flex-grow-1"
              multiline
              minRows={2}
              maxRows={6}
              size="small"
              placeholder="Write a message…"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />
            <Button
              variant="contained"
              owner={owner}
              className={`room-thread__send ${
                owner === "client" ? "buyer-room__btn" : ""
              }`}
              disabled={isSending || !draft.trim()}
              onClick={send}
              startIcon={<SendIcon />}
            >
              <Typography owner={owner} variant="button">
                Send
              </Typography>
            </Button>
          </div>
          {sendError && (
            <Typography owner={owner} variant="caption" color="error">
              {sendError}
            </Typography>
          )}
          <Typography owner={owner} variant="caption" className="room-thread__note">
            Everyone in this room is notified by email when you post. Email
            replies are not added to the conversation.
          </Typography>
        </div>
      )}
    </div>
  );
}
